import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";
import { WordReveal } from "./WordReveal";
import { EASE } from "../utils/motion";
type HeroVariant = "full" | "split" | "editorial" | "type";
type PageHeroProps = {
  eyebrow: string;
  title: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  breadcrumb: string;
  variant?: HeroVariant;
};
export function PageHero({
  eyebrow,
  title,
  description,
  image,
  imageAlt = "",
  breadcrumb,
  variant = "full"
}: PageHeroProps) {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, reduce ? 1 : 0]);
  const dark = variant === "full" || variant === "split" || variant === "type";
  const crumb = <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${dark ? "text-white/60" : "text-[#6b7378]"}`} data-id="element-313" data-mp="3c854ad02d9fa">
      <Link to="/" className={`transition-colors duration-200 ${dark ? "hover:text-white" : "hover:text-[#061f38]"}`} data-id="element-314" data-mp="37gvk3rxaayas">Home</Link>
      <ChevronRightIcon size={13} data-id="element-315" data-mp="3l7ij7l6yiq7e" />
      <span className={dark ? "text-white/90" : "text-[#061f38]"} data-id="element-316" data-mp="3lamu11exgk71">{breadcrumb}</span>
    </nav>;
  const eyebrowEl = <motion.p initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: reduce ? 0.01 : 0.6,
    delay: reduce ? 0 : 0.1,
    ease: EASE
  }} className={`text-xs font-semibold tracking-[0.18em] ${dark ? "text-[#f0b1b9]" : "text-[#bb1e35]"}`} data-id="element-317" data-mp="3v6ps9w6nryop">
      {eyebrow}
    </motion.p>;
  const descEl = description ? <motion.p initial={{
    opacity: 0,
    y: reduce ? 0 : 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: reduce ? 0.01 : 0.7,
    delay: reduce ? 0 : 0.45,
    ease: EASE
  }} className={`max-w-md text-lg leading-8 ${dark ? "text-white/75" : "text-[#3d4b57]"}`} data-id="element-318" data-mp="1vunf13omo0jh">
      {description}
    </motion.p> : null;
  if (variant === "split") {
    return <section ref={ref} className="grid min-h-[92vh] bg-[#061f38] text-white lg:grid-cols-12" data-id="element-319" data-mp="2ul21kh2tss5w">
        <div className="flex flex-col justify-between px-5 pb-12 pt-28 md:px-9 lg:col-span-5 lg:pb-16" data-id="element-320" data-mp="2zfpjfx3bqobi">
          {crumb}
          <motion.div style={{
          y: textY
        }} className="mt-16" data-id="element-321" data-mp="38i8hm3w60crf">
            {eyebrowEl}
            <WordReveal as="h1" immediate delay={0.15} text={title} className="mt-6 text-5xl font-medium leading-[0.95] tracking-[-0.06em] md:text-7xl" data-id="element-322" data-mp="3d970nb8jfoyd" />
            <div className="mt-8" data-id="element-323" data-mp="3i6ytc7h0bf3m">{descEl}</div>
          </motion.div>
        </div>
        <motion.div initial={{
        clipPath: reduce ? "inset(0 0 0 0)" : "inset(0 0 0 100%)"
      }} animate={{
        clipPath: "inset(0 0 0 0%)"
      }} transition={{
        duration: reduce ? 0.01 : 1,
        ease: EASE
      }} className="relative min-h-[50vh] overflow-hidden lg:col-span-7" data-id="element-324" data-mp="3n8fqpt0a8jfg">
          {image && <motion.img src={image} alt={imageAlt} fetchPriority="high" style={{
          y: imageY,
          scale: 1.1
        }} className="absolute inset-0 h-full w-full object-cover" data-id="element-325" data-mp="1bk17srdl2z1a" />}
        </motion.div>
      </section>;
  }
  if (variant === "editorial") {
    return <section ref={ref} className="bg-[#fbfaf6] pt-28 text-[#061f38]" data-id="element-326" data-mp="11nemy2l9c7z9">
        <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-327" data-mp="1z9vjv6q2wjkh">
          {crumb}
          <div className="mt-14 grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-328" data-mp="1u9zbywe9g7zn">
            <div className="lg:col-span-8" data-id="element-329" data-mp="1ucvbuq9o56qq">
              {eyebrowEl}
              <WordReveal as="h1" immediate delay={0.1} text={title} className="mt-6 text-[clamp(3rem,7.5vw,7.5rem)] font-medium leading-[0.9] tracking-[-0.07em]" data-id="element-330" data-mp="1pi7rn3lp6yhy" />
            </div>
            <div className="pb-3 lg:col-span-4" data-id="element-331" data-mp="2i0vcchws8bii">{descEl}</div>
          </div>
        </div>
        {image && <motion.div initial={{
        clipPath: reduce ? "inset(0 0 0 0)" : "inset(12% 6% 0% 6%)"
      }} animate={{
        clipPath: "inset(0% 0% 0% 0%)"
      }} transition={{
        duration: reduce ? 0.01 : 1.1,
        delay: reduce ? 0 : 0.2,
        ease: EASE
      }} className="relative mt-14 h-[62vh] min-h-[380px] overflow-hidden" data-id="element-332" data-mp="2d7tv4393rzvr">
            <motion.img src={image} alt={imageAlt} fetchPriority="high" style={{
          y: imageY,
          scale: 1.12
        }} className="absolute inset-0 h-full w-full object-cover" data-id="element-333" data-mp="1nuh9o1ynxgxe" />
          </motion.div>}
      </section>;
  }
  if (variant === "type") {
    return <section ref={ref} className="relative overflow-hidden bg-[#061f38] pb-20 pt-28 text-white md:pb-28" data-id="element-334" data-mp="2lhhdlrrliwhv">
        <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-335" data-mp="26stlxwkvf6x7">
          {crumb}
          <motion.div style={{
          y: textY,
          opacity: fade
        }} className="mt-20 md:mt-28" data-id="element-336" data-mp="2bqv6klxy3eas">
            {eyebrowEl}
            <WordReveal as="h1" immediate delay={0.1} text={title} className="mt-6 max-w-5xl text-[clamp(3.2rem,8vw,8.5rem)] font-medium leading-[0.88] tracking-[-0.075em]" data-id="element-337" data-mp="2zg7fdkpd1kck" />
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: reduce ? 0.01 : 0.9,
            delay: reduce ? 0 : 0.5,
            ease: EASE
          }} className="mt-12 h-px w-full origin-left bg-white/25" data-id="element-338" data-mp="ssgq7ci9yuwb" />
            <div className="mt-8" data-id="element-339" data-mp="12i0b0npjsytt">{descEl}</div>
          </motion.div>
        </div>
      </section>;
  }
  return <section ref={ref} className="relative flex min-h-[86vh] items-end overflow-hidden bg-[#061f38] text-white" data-id="element-340" data-mp="xix5711quoks">
      {image && <motion.img src={image} alt={imageAlt} fetchPriority="high" initial={{
      scale: reduce ? 1.1 : 1.2
    }} animate={{
      scale: 1.1
    }} transition={{
      duration: reduce ? 0.01 : 1.4,
      ease: EASE
    }} style={{
      y: imageY
    }} className="absolute inset-0 h-full w-full object-cover" data-id="element-341" data-mp="9u4gnpngbj07" />}
      <div className="absolute inset-0 bg-[#061f38]/40" data-id="element-342" data-mp="esfppy5x9phd" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[#061f38]/30" data-id="element-343" data-mp="ev1yg14jub16" />
      <motion.div style={{
      y: textY,
      opacity: fade
    }} className="relative mx-auto w-full max-w-[1440px] px-5 pb-14 pt-32 md:px-9 md:pb-20" data-id="element-344" data-mp="1u7v32s4sj2m6">
        {crumb}
        <div className="mt-10" data-id="element-345" data-mp="mxgifogfjjfz">{eyebrowEl}</div>
        <WordReveal as="h1" immediate delay={0.15} text={title} className="mt-6 max-w-4xl text-5xl font-medium leading-[0.92] tracking-[-0.065em] md:text-8xl" data-id="element-346" data-mp="mwozbd40re6l" />
        <div className="mt-8" data-id="element-347" data-mp="wuuulbvblm2h">{descEl}</div>
      </motion.div>
    </section>;
}